import React, { useEffect, useState } from "react";
import { Empty, Modal, Checkbox, Form, Input, Button } from "antd";
import SearchAndFilter from "../../../../components/Common/SearchFilter";
import { useTrustedNetwork } from "../../../../context/TrustedNetworkContext";
import { Loader, Dialog, Flex, Text, Divider } from "@fluentui/react-northstar";
import defaultSchoolLogo from "../../../../Assets/images/DefaultSchoolLogo.png";
import TrustedNetworkCard from "./TrustedNetworkCard";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import SharedSchoolInfoDialog from "./SharedSchoolInfoDialog";
import SchoolBranchesDialog from "./SchoolBranchesDialog";
import { useTranslation } from "react-i18next";

// Custom hook for responsive behavior
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

const TrustedNetworks = ({ user: propUser }) => {
  const { t } = useTranslation();
  const translation = t("trustedNetwork");
  const {
    getTrustedNetworks,
    trustedNetworks,
    isLoadingTrustedNetworks,
    endTrustedNetwork,
    isEndingTrustedNetwork,
    user: contextUser,
  } = useTrustedNetwork();

  const user = propUser || contextUser;
  const { width } = useWindowSize();
  const isMediumOrSmallScreen = width <= 1099;

  const [confirmEndDialog, setConfirmEndDialog] = useState({
    open: false,
    networkId: null,
  });
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const [isBranchesDialogOpen, setIsBranchesDialogOpen] = useState(false);
  const [selectedSchoolForBranches, setSelectedSchoolForBranches] =
    useState(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredNetworks, setFilteredNetworks] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    schoolNames: [],
    adminNames: [],
  });

  const isAdmin = (network) => {
    const normalizedSlug = user.slug.replace(/^\?slug=/, "");
    return network.schoolId === normalizedSlug;
  };

  const handleInfoClick = (schoolInfo, network) => {
    const normalizedSlug = user.slug.replace(/^\?slug=/, "");

    const targetSchoolId =
      network.schoolId === normalizedSlug
        ? network.trustedSchoolId
        : network.schoolId;

    setSelectedSchool({
      ...schoolInfo,
      schoolId: targetSchoolId,
    });
    setIsInfoDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsInfoDialogOpen(false);
    setSelectedSchool(null);
  };

  useEffect(() => {
    getTrustedNetworks();
    // eslint-disable-next-line
  }, []);

  // Update filtered networks when trustedNetworks or search query changes
  useEffect(() => {
    if (!trustedNetworks) {
      setFilteredNetworks([]);
      return;
    }

    let filtered = trustedNetworks;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (network) =>
          network.schoolName?.toLowerCase().includes(query) ||
          network.superAdminName?.toLowerCase().includes(query) ||
          network.superAdminEmail?.toLowerCase().includes(query) ||
          network.schoolAddress?.toLowerCase().includes(query)
      );
    }

    // Apply school names filter
    if (selectedFilters.schoolNames && selectedFilters.schoolNames.length > 0) {
      filtered = filtered.filter((network) =>
        selectedFilters.schoolNames.includes(network.schoolName)
      );
    }

    // Apply admin names filter
    if (selectedFilters.adminNames && selectedFilters.adminNames.length > 0) {
      filtered = filtered.filter((network) =>
        selectedFilters.adminNames.includes(network.superAdminName)
      );
    }

    setFilteredNetworks(filtered);
  }, [trustedNetworks, searchQuery, selectedFilters]);

  const handleEndNetwork = (network) => {
    setConfirmEndDialog({
      open: true,
      networkId: network.id,
    });
  };

  const handleConfirmEnd = async () => {
    await endTrustedNetwork(confirmEndDialog.networkId);
    setConfirmEndDialog({ open: false, networkId: null });
  };

  const handleViewBranches = (network) => {
    const normalizedSlug = user.slug.replace(/^\?slug=/, "");

    const targetSchoolId =
      network.schoolId === normalizedSlug
        ? network.trustedSchoolId
        : network.schoolId;

    setSelectedSchoolForBranches({
      schoolId: targetSchoolId,
      schoolName: network.schoolName,
    });
    setIsBranchesDialogOpen(true);
  };

  const handleCloseBranchesDialog = () => {
    setIsBranchesDialogOpen(false);
    setSelectedSchoolForBranches(null);
  };

  // Search and filter handlers
  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  // Generate filter options from trustedNetworks data
  const filterOptions = React.useMemo(() => {
    if (!trustedNetworks || trustedNetworks.length === 0) return [];

    const schoolNames = [
      ...new Set(
        trustedNetworks.map((network) => network.schoolName).filter(Boolean)
      ),
    ];
    const adminNames = [
      ...new Set(
        trustedNetworks.map((network) => network.superAdminName).filter(Boolean)
      ),
    ];

    return [
      {
        id: "schoolNames",
        title: "School Names",
        options: schoolNames.map((name) => ({ label: name, value: name })),
        search: true,
      },
      {
        id: "adminNames",
        title: "Admin Names",
        options: adminNames.map((name) => ({ label: name, value: name })),
        search: true,
      },
    ];
  }, [trustedNetworks]);

  const handleFilterChange = (filterKey, selectedValues) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterKey]: selectedValues,
    }));
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({
      schoolNames: [],
      adminNames: [],
    });
  };

  if (isLoadingTrustedNetworks) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Loader size="medium" />
      </div>
    );
  }

  if (!trustedNetworks || trustedNetworks.length === 0) {
    return (
      <div
        className="empty-state-container"
        style={{ textAlign: "center", padding: "40px 0" }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            translation.noNetworksFound || "No trusted networks available"
          }
        />
      </div>
    );
  }
  return (
    <>
      {/* Centered Search and Filter Bar */}
      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}
      >
        <div style={{ minWidth: 320, maxWidth: 600, width: "100%" }}>
          <SearchAndFilter
            searchValue={searchQuery}
            onSearchChange={handleSearch}
            searchPlaceholder={
              translation.searchNetworksPlaceholder ||
              "Search trusted networks..."
            }
            filterOptions={filterOptions}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            filterButtonText={translation.filter || "Filter"}
            translations={{
              clear: translation.clear || "Clear",
              apply: translation.apply || "Apply",
              notFound: translation.notFound || "No options found",
            }}
          />
        </div>
      </div>
      <div
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          paddingRight: isMediumOrSmallScreen ? "8px" : "0",
        }}
      >
        {filteredNetworks.length === 0 && trustedNetworks.length > 0 ? (
          <div
            className="empty-state-container"
            style={{ textAlign: "center", padding: "40px 0" }}
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={translation.noNetworksFoundWithFilters}
            />
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                width <= 500
                  ? "repeat(auto-fit, minmax(200px, 1fr))"
                  : "repeat(auto-fit, minmax(250px, 380px))",
              gap: "clamp(6px, 1vw, 16px)",
              justifyContent: "center",
            }}
          >
            {filteredNetworks.map((network) => {
              const userIsAdmin = isAdmin(network);
              return (
                <div key={network.id} style={{ display: "flex" }}>
                  <TrustedNetworkCard
                    key={network.id}
                    schoolName={network.schoolName}
                    schoolLogo={network.schoolLogo}
                    teacherCount={network.teacherCount}
                    collectionsCount={network.collectionsCount}
                    spinWheelsCount={network.spinWheelsCount}
                    contactNumber={network.contactNumber}
                    schoolAddress={network.schoolAddress}
                    superAdminName={network.superAdminName}
                    superAdminEmail={network.superAdminEmail}
                    teachersCount={network.teachersCount}
                    studentsCount={network.studentsCount}
                    onEndNetwork={() => handleEndNetwork(network)}
                    isEnding={network.isEnding}
                    onInfoClick={(schoolInfo) =>
                      handleInfoClick(schoolInfo, network)
                    }
                    onViewBranches={() => handleViewBranches(network)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmationDialog
        open={confirmEndDialog.open}
        onCancel={() => setConfirmEndDialog({ open: false, networkId: null })}
        onConfirm={handleConfirmEnd}
        title={translation.endNetworkTitle}
        content={translation.endNetworkContent}
        confirmButton={{
          content: translation.endNetworkConfirm,
          danger: true,
        }}
      />
      <SharedSchoolInfoDialog
        open={isInfoDialogOpen}
        onClose={handleCloseDialog}
        schoolInfo={selectedSchool}
      />

      <SchoolBranchesDialog
        open={isBranchesDialogOpen}
        onClose={handleCloseBranchesDialog}
        schoolId={selectedSchoolForBranches?.schoolId}
        schoolName={selectedSchoolForBranches?.schoolName}
        user={user}
      />
    </>
  );
};

export default TrustedNetworks;
